// piano-frontend/src/contexts/DialogProvider.js
// 네이티브 window.confirm / window.alert 를 대체하는 앱 공용 커스텀 팝업.
// - confirm(opts) : 확인/취소 다이얼로그 (Promise<boolean>) — window.confirm 대체
// - alert(opts)   : 확인 버튼만 있는 안내 다이얼로그 (Promise) — 중요 안내 대체
// - toast(msg, severity) : 스낵바(토스트) — 단순 window.alert 대체
import { createContext, useContext, useState, useCallback } from 'react';
import {
  DialogTitle, DialogContent, DialogActions,
  Button, Typography, Snackbar, Alert,
} from '@mui/material';
import Dialog from '../components/AppDialog';

const DialogContext = createContext(null);

export function useDialog() {
  const ctx = useContext(DialogContext);
  if (!ctx) {
    throw new Error('useDialog 는 <DialogProvider> 내부에서만 사용할 수 있습니다.');
  }
  return ctx;
}

export function DialogProvider({ children }) {
  // 스낵바(토스트) — window.alert 대체
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const toast = useCallback((message, severity = 'success') => {
    const cleaned = typeof message === 'string' ? message.replace(/^(✅|❌|⚠️|ℹ️)\s*/, '') : message;
    setSnackbar({ open: true, message: cleaned, severity });
  }, []);
  const closeSnackbar = (_, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar(s => ({ ...s, open: false }));
  };

  // 확인/안내 다이얼로그 — window.confirm 대체 (Promise 기반)
  const [confirmState, setConfirmState] = useState({
    open: false, title: '', message: '', confirmText: '확인', cancelText: '취소',
    color: 'primary', severity: null, hideCancel: false, resolve: null,
  });
  const confirm = useCallback((opts) => new Promise(resolve => {
    const normalized = typeof opts === 'string' ? { message: opts } : (opts || {});
    setConfirmState({
      open: true, title: '', message: '', confirmText: '확인', cancelText: '취소',
      color: 'primary', severity: null, hideCancel: false, ...normalized, resolve,
    });
  }), []);
  const closeConfirm = (result) => {
    confirmState.resolve?.(result);
    setConfirmState(s => ({ ...s, open: false, resolve: null }));
  };
  // 안내 전용(확인 버튼만)
  const alert = useCallback((opts) => {
    const normalized = typeof opts === 'string' ? { message: opts } : (opts || {});
    return confirm({ hideCancel: true, ...normalized });
  }, [confirm]);

  return (
    <DialogContext.Provider value={{ confirm, alert, toast }}>
      {children}

      {/* 전역 스낵바(토스트) — window.alert 대체 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'error' ? 6000 : 3500}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', whiteSpace: 'pre-line' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* 전역 확인/안내 다이얼로그 — window.confirm 대체 */}
      <Dialog open={confirmState.open} onClose={() => closeConfirm(false)} maxWidth="xs" fullWidth>
        {confirmState.title && <DialogTitle sx={{ fontWeight: 600 }}>{confirmState.title}</DialogTitle>}
        <DialogContent>
          {confirmState.severity ? (
            <Alert severity={confirmState.severity} sx={{ whiteSpace: 'pre-line' }}>{confirmState.message}</Alert>
          ) : (
            <Typography sx={{ whiteSpace: 'pre-line' }}>{confirmState.message}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          {!confirmState.hideCancel && <Button onClick={() => closeConfirm(false)}>{confirmState.cancelText}</Button>}
          <Button variant="contained" color={confirmState.color} onClick={() => closeConfirm(true)} autoFocus>
            {confirmState.confirmText}
          </Button>
        </DialogActions>
      </Dialog>
    </DialogContext.Provider>
  );
}
